import { useEditorVisible } from "../../aee/EditorVisibility";
import { createPortal } from "react-dom";
import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";
import type { Page, Project } from "./model";
import { ProfileMenu } from "./ProfileMenu";
export function JournalBreadcrumbs({
  project,
  page,
}: {
  project?: Project;
  page?: Page;
}) {
  const trail: Page[] = [];
  let parent = page?.parentId;
  const visited = new Set<string>();
  while (parent && project && !visited.has(parent)) {
    visited.add(parent);
    const p = project.pages.find((p) => p.id === parent);
    if (!p) break;
    trail.unshift(p);
    parent = p.parentId;
  }
  return (
    <nav className="nb-breadcrumbs" aria-label="Document breadcrumbs">
      {project && (
        <>
          <Link to={`/app/project/${project.id}`}>{project.name}</Link>
          {trail.map((p) => (
            <span key={p.id}>
              {" "}
              /{" "}
              <Link to={`/app/project/${project.id}/notebook/${p.id}`}>
                {p.title}
              </Link>
            </span>
          ))}
          {page && <span> / {page.title}</span>}
        </>
      )}
    </nav>
  );
}
export function JournalAppBar({
  project,
  page,
  notice,
  onShare,
  onHistory,
  environmentHome = false,
  fileName,
  showNotice = true,
  sharing,
  projectLink,
}: {
  project?: Project;
  page?: Page;
  environmentHome?: boolean;
  fileName?: string;
  showNotice?: boolean;
  sharing?: ReactNode;
  projectLink?:string;
  notice: string;
  onShare?: () => void;
  onHistory?: () => void;
}) {
  return (
    <header className="nb-app-bar">
      <Link
        to="/app"
        className="nb-home-icon"
        aria-label="Engineering Environment Home"
        title="Engineering Environment Home"
      >
        <svg viewBox="0 0 24 24">
          <path d="m3 10 9-7 9 7v11h-7v-7h-4v7H3Z" />
        </svg>
      </Link>
      <span className="nb-app-brand">{environmentHome ? "Aldapa Engineering Environment" : "NOTEBOOK"}</span>
      {environmentHome && project ? <nav className="nb-breadcrumbs nb-header-trail" aria-label="Workspace breadcrumb" title={`Aldapa Engineering Environment > ${project.name}${fileName?` > ${fileName}`:''}`}><span aria-hidden="true"> › </span><Link to={projectLink||`/app/project/${project.id}`}>{project.name}</Link>{fileName&&<><span aria-hidden="true"> › </span><span aria-current="page">{fileName}</span></>}</nav> : <JournalBreadcrumbs project={project} page={page} />}
      {showNotice && <span className="nb-app-save" role="status" title={notice}>
        {notice.startsWith("Saved") ? "● Saved" : notice}
      </span>}
      <div className="nb-app-controls">
        {sharing || (onShare && (
          <button title="Export a project copy to share" onClick={onShare}>
            Share
          </button>
        ))}
        {onHistory && <button onClick={onHistory}>History</button>}
        <ProfileMenu />
      </div>
    </header>
  );
}
export function JournalStatusBar({
  sheets,
  revision,
  notice,
  currentSheet = 1,
  notebookPosition,
  cloud = false,
  fileName,
}: {
  cloud?:boolean;
  fileName?:string;
  notebookPosition?:{current:number;total:number};
  currentSheet?: number;
  sheets: number;
  revision: number;
  notice: string;
}) {
  return (
    <footer className="nb-status-bar">
      <span>
        {notebookPosition?`Page ${notebookPosition.current} of ${notebookPosition.total} · `:''}Sheet {Math.min(currentSheet, sheets)} of {sheets} · US Letter · 100% ·
        {`Revision ${revision}`}
      </span>
      <span role="status">{notice}</span>
      <span>{fileName || (cloud ? "" : "Local")}</span>
    </footer>
  );
}
function storedWidth(key: string, fallback: number, min = 160, max = 420) {
  try {
    const n = Number(localStorage.getItem(key));
    return n >= min && n <= max ? n : fallback;
  } catch {
    return fallback;
  }
}
export function JournalAppShell({
  light,
  chromeHost,
  navigation,
  equationPanel = false,
  aeiPanel = false,
  bar,
  menus,
  ribbon,
  ribbonHeight = 52,
  rail,
  sidebar,
  inspector,
  treeVisible,
  inspectorVisible,
  status,
  statusHost,
  children,
  onEditorScroll,
  embedded = false,
}: {
  chromeHost?: HTMLElement | null;
  navigation?: ReactNode;
  equationPanel?: boolean;
  /** The AEI assistant uses its own wider, remembered width (default 360, 300-650). */
  aeiPanel?: boolean;
  embedded?: boolean;
  light: boolean;
  bar: ReactNode;
  menus: ReactNode;
  ribbon?: ReactNode;
  ribbonHeight?: number;
  rail?: ReactNode;
  sidebar: ReactNode;
  inspector: ReactNode;
  treeVisible: boolean;
  inspectorVisible: boolean;
  status: ReactNode;
  statusHost?: HTMLElement | null;
  children: ReactNode;
  onEditorScroll?: (element: HTMLElement) => void;
}) {
  const visible = useEditorVisible();
  const [left, setLeft] = useState(() =>
    storedWidth("notebook-tree-width", 210),
  );
  const [normalRight, setNormalRight] = useState(() =>
    storedWidth("notebook-inspector-width", 270),
  );
  const [equationRight,setEquationRight]=useState(()=>storedWidth('notebook-equation-width',320));
  const [aeiRight, setAeiRight] = useState(() => storedWidth("notebook-aei-width", 360, 300, 650));
  const right=aeiPanel?aeiRight:equationPanel?equationRight:normalRight;
  const setRight=aeiPanel?setAeiRight:equationPanel?setEquationRight:setNormalRight;
  const limits = (side: "left" | "right") =>
    side === "left"
      ? { min: 160, max: 420, key: "notebook-tree-width" }
      : aeiPanel
        ? { min: 300, max: 650, key: "notebook-aei-width" }
        : equationPanel
          ? { min: 280, max: 420, key: "notebook-equation-width" }
          : { min: 160, max: 420, key: "notebook-inspector-width" };
  const resize = (side: "left" | "right", start: number) => {
    const value = side === "left" ? left : right;
    const { min, max, key } = limits(side);
    const change = (x: number) => {
      const width = Math.max(min, Math.min(max, value + (x - start) * (side === "left" ? 1 : -1)));
      if (side === "left") setLeft(width);
      else setRight(width);
      try {
        localStorage.setItem(key, String(width));
      } catch {
        /* Current session can still resize. */
      }
    };
    const move = (e: PointerEvent) => change(e.clientX);
    const end = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end, { once: true });
    window.addEventListener("pointercancel", end, { once: true });
  };
  const separator = (side: "left" | "right") => (
    <div
      className={`nb-panel-resizer nb-resize-${side}`}
      role="separator"
      aria-label={`Resize ${side === "left" ? "project tree" : "inspector"}`}
      aria-orientation="vertical"
      aria-valuemin={limits(side).min}
      aria-valuemax={limits(side).max}
      aria-valuenow={side === "left" ? left : right}
      tabIndex={0}
      onPointerDown={(e) => {
        e.preventDefault();
        resize(side, e.clientX);
      }}
      onKeyDown={(e) => {
        if (!["ArrowLeft", "ArrowRight"].includes(e.key)) return;
        e.preventDefault();
        const delta =
          (e.key === "ArrowRight" ? 16 : -16) * (side === "left" ? 1 : -1);
        const { min, max, key } = limits(side);
        const width = Math.max(min, Math.min(max, (side === "left" ? left : right) + delta));
        if (side === "left") setLeft(width);
        else setRight(width);
        try {
          localStorage.setItem(key, String(width));
        } catch {
          /* Local preference is optional. */
        }
      }}
    />
  );
  return (
    <section className={`notebook nb-app ${embedded ? "nb-embedded" : ""} ${light ? "nb-light" : ""}`} style={{"--ribbon-height":`${ribbonHeight}px`} as CSSProperties}>
      <a className="skip-link" href="#notebook-editor">
        Skip to document
      </a>
      {bar}
      {chromeHost ? createPortal(<div className="aee-retained-chrome" hidden={!visible}>{menus}{ribbon}</div>, chromeHost) : <>{menus}{ribbon}</>}
      {navigation}
      <div
        className={`nb-app-workspace ${rail ? "has-rail" : ""} ${treeVisible ? "has-tree" : ""} ${inspectorVisible ? "has-inspector" : ""}`}
        style={
          {
            "--tree-width": `${left}px`,
            // The assistant never takes more than 55% of the window, so the page stays usable on small screens.
            "--inspector-width": aeiPanel ? `min(${right}px, 55vw)` : `${right}px`,
          } as CSSProperties
        }
      >
        {rail}
        {treeVisible && <aside className="nb-project-panel">{sidebar}</aside>}
        {treeVisible && separator("left")}
        <main
          className="nb-app-editor"
          id={visible ? "notebook-editor" : undefined}
          onScroll={(e) => onEditorScroll?.(e.currentTarget)}
        >
          {children}
        </main>
        {inspectorVisible && separator("right")}
        {inspectorVisible && (
          <aside className={`nb-inspector-panel ${equationPanel?'has-equation-editor':''}`}>{inspector}</aside>
        )}
      </div>
      {statusHost ? (visible ? createPortal(status, statusHost) : null) : status}
    </section>
  );
}
